/**
 * THE accepted-upload contract (Rule #14 — one definition, read everywhere).
 *
 * The API gates every upload three ways — declared MIME type, magic bytes, and file
 * extension — and the browser's `accept` attribute decides what a barrister can even
 * pick. Those lists used to be written out by hand at each site and had drifted apart:
 * the main upload modal offered .rtf/.xlsx/.xls/.pptx that the API rejects, and omitted
 * every image format the API accepts — so a photo of a signed order could not be chosen
 * through the file picker at all (it could only be dragged in, which bypasses `accept`).
 *
 * Both sides now read these constants, so the picker and the validator cannot disagree.
 */

/** MIME types the API will store. */
export const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/plain', // .txt
] as const;

/** Extension → the MIME types that may legitimately declare it. */
export const DOCUMENT_EXTENSION_TO_MIME: Record<string, readonly string[]> = {
  '.pdf': ['application/pdf'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.doc': ['application/msword'],
  '.jpeg': ['image/jpeg'],
  '.jpg': ['image/jpeg', 'image/jpg'],
  '.png': ['image/png'],
  '.gif': ['image/gif'],
  '.webp': ['image/webp'],
  '.txt': ['text/plain'],
};

/** Every accepted extension, e.g. for an error message. */
export const DOCUMENT_EXTENSIONS = Object.keys(DOCUMENT_EXTENSION_TO_MIME);

/**
 * Value for a file input's `accept`. Extensions AND MIME types are listed: some
 * browsers match one, some the other, and a photo picked from a phone camera roll
 * often arrives with an extension but no useful type.
 */
export const DOCUMENT_ACCEPT_ATTR = [...DOCUMENT_EXTENSIONS, ...DOCUMENT_MIME_TYPES].join(',');

/** Human-readable list — keep UI hint text and API errors saying the same thing. */
export const DOCUMENT_FORMATS_LABEL = 'PDF, Word, JPEG, PNG, GIF, WEBP, TXT';
